import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import SignUp from './pages/SignUp/SignUp';
import Dictionary from './pages/Dictionary/Dictionary';
import RecordDealWriter from './pages/record-deal-writer/record-deal-writer';
import Home from './pages/Home/Home';
import ToolsLanding from './pages/user-menu/ToolsLanding';
import DocumentInterpreter from './pages/contract-interpreter/interpreter';
import AnalysisDashboard from './pages/analysis-dashboard/AnalysisDashboard';
import ContractGenerationDashboard from './pages/contract-generation-dashboard/ContractGenerationDashboard';
import ContractGenerator from './pages/contract-generator/ContractGenerator';
import BuyCredits from './pages/BuyCredits/BuyCredits';
import PaymentSuccess from './pages/PaymentSuccess/PaymentSuccess';
import Sidebar from './components/Sidebar';
import NotFound from './pages/NotFound/NotFound';
import './styles/darkMode.css';
import './App.css';
import OnboardingPage from './pages/Onboarding/OnboardingPage';
import AccountSettings from './pages/AccountSettings/AccountSettings';
import DPA from './pages/DPA/DPA';
import Terms from './pages/Terms/Terms';
import DMCA from './pages/DMCA/DMCA';
import Privacy from './pages/Privacy/Privacy';
import Subprocessors from './pages/Subprocessors/Subprocessors';
import LegalNotices from './pages/LegalNotices/LegalNotices';

// Utility to decode JWT and check expiration
function decodeJwt(token: string): any {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(function (c) {
          return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
        })
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
}

function isTokenExpired(idToken: string): boolean {
  const decoded = decodeJwt(idToken);
  if (!decoded || !decoded.exp) return true;
  const now = Math.floor(Date.now() / 1000);
  return decoded.exp < now;
}

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const userStr = localStorage.getItem('user');
  let isAuthenticated = false;
  let isExpired = false;
  if (userStr) {
    try {
      const user = JSON.parse(userStr);
      if (user.idToken) {
        isExpired = isTokenExpired(user.idToken);
        isAuthenticated = !isExpired;
      }
    } catch {}
  }
  if (!isAuthenticated) {
    // Remove user from storage if expired
    localStorage.removeItem('user');
    return <Navigate to="/signup" state={{ tokenExpired: isExpired }} />;
  }

  return <>{children}</>;
};

const App: React.FC = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const user = localStorage.getItem('user');
    const authenticated = !!user;
    setIsAuthenticated(authenticated);
    
    // Sidebar will remain closed by default
  }, []);

  const handleLogin = () => {
    setIsAuthenticated(true);
    setIsSidebarOpen(false);
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    setIsAuthenticated(false);
    setIsSidebarOpen(false);
  };

  const handleSidebarToggle = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  return (
    <Router>
      <div className="app-container">
        {/* Sidebar */}
        {isAuthenticated && (
          <Sidebar 
            isOpen={isSidebarOpen}
            onToggle={handleSidebarToggle}
            onLogout={handleLogout}
          />
        )}

        {/* Main Content */}
        <main className={`main-content ${isAuthenticated ? 'with-sidebar' : ''}`}>
          <Routes>
            <Route path="/" element={
              isAuthenticated ? <Navigate to="/tools" /> : <Home />
            } />
            <Route path="/signup" element={<SignUp onLogin={handleLogin} />} />
            <Route path="/onboarding" element={<OnboardingPage />} />
            <Route
              path="/account-settings"
              element={
                <ProtectedRoute>
                  <AccountSettings />
                </ProtectedRoute>
              }
            />
            <Route path="/dpa" element={<DPA />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/dmca" element={<DMCA />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/subprocessors" element={<Subprocessors />} />
            <Route path="/legal" element={<LegalNotices />} />
            <Route
              path="/dictionary"
              element={
                <ProtectedRoute>
                  <Dictionary />
                </ProtectedRoute>
              }
            />
            <Route
              path="/interpreter"
              element={
                <ProtectedRoute>
                  <DocumentInterpreter />
                </ProtectedRoute>
              }
            />
            <Route
              path="/record-deal-form"
              element={
                <ProtectedRoute>
                  <RecordDealWriter />
                </ProtectedRoute>
              }
            />
            <Route
              path="/tools"
              element={
                <ProtectedRoute>
                  <ToolsLanding />
                </ProtectedRoute>
              }
            />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <AnalysisDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/contract-generator"
              element={
                <ProtectedRoute>
                  <ContractGenerator />
                </ProtectedRoute>
              }
            />
            <Route
              path="/contract-generations"
              element={
                <ProtectedRoute>
                  <ContractGenerationDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/buy-credits"
              element={
                <ProtectedRoute>
                  <BuyCredits />
                </ProtectedRoute>
              }
            />
            <Route
              path="/payment-success"
              element={
                <ProtectedRoute>
                  <PaymentSuccess />
                </ProtectedRoute>
              }
            />
            {/* Catch-all route for 404 errors */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
      </div>
    </Router>
  );
};

export default App;
